import React, { useEffect, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import { BreakoutSignal } from "../../lib/interface/BreakoutSignal.interface";
import {
  createSeriesMarkers,
  Time,
  ISeriesMarkersPluginApi,
} from "lightweight-charts";

/**
 * Props specific to Breakout Signals indicator
 */
export interface BreakoutSignalsProps extends BaseIndicatorProps {
  breakoutSignals: BreakoutSignal[];
  formatTime: (timestamp: number) => number;
  longColor?: string;
  shortColor?: string;
  longLabel?: string;
  shortLabel?: string;
}

/**
 * Breakout Signals Indicator Component
 * Shows breakout buy/sell signal markers on the chart
 */
export const BreakoutSignalsIndicator: React.FC<BreakoutSignalsProps> = ({
  chartInstance,
  candleSeries,
  isEnabled,
  breakoutSignals,
  formatTime,
  longColor,
  shortColor,
  longLabel,
  shortLabel,
}) => {
  const seriesMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const longColorValue = longColor ?? "#22c55e";
  const shortColorValue = shortColor ?? "#ef4444";
  const longMarkerText = longLabel ?? "BUY";
  const shortMarkerText = shortLabel ?? "SELL";

  // Initialize the series markers API ONCE when component mounts
  useEffect(() => {
    if (!chartInstance || !candleSeries) {
      return;
    }

    // Create the series markers API reference only once
    if (!seriesMarkersRef.current) {
      try {
        seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
      } catch {
        return;
      }
    }
  }, [chartInstance, candleSeries]);

  // Update markers when breakoutSignals or isEnabled changes
  useEffect(() => {
    if (!chartInstance || !candleSeries || !seriesMarkersRef.current) {
      return;
    }

    // If disabled, clear markers
    if (!isEnabled) {
      try {
        seriesMarkersRef.current.setMarkers([]);
      } catch {}
      return;
    }

    // If no breakout signals to display, clear markers
    if (breakoutSignals.length === 0) {
      try {
        seriesMarkersRef.current.setMarkers([]);
      } catch {}
      return;
    }

    // Create markers for breakout signals
    const chartMarkers = breakoutSignals.map((signal) => {
      const action = signal.action ?? signal.signal;
      const isLong = action === "LONG";
      return {
        time: formatTime(signal.timestamp) as Time,
        position: isLong ? ("belowBar" as const) : ("aboveBar" as const),
        color: isLong ? longColorValue : shortColorValue,
        shape: isLong ? ("arrowUp" as const) : ("arrowDown" as const),
        text: isLong ? longMarkerText : shortMarkerText,
      };
    });

    // Use setMarkers to update the existing markers API
    try {
      seriesMarkersRef.current.setMarkers(chartMarkers);
    } catch {}
  }, [
    breakoutSignals,
    isEnabled,
    formatTime,
    chartInstance,
    candleSeries,
    longColorValue,
    shortColorValue,
    longMarkerText,
    shortMarkerText,
  ]);

  // Cleanup effect to clear markers when component unmounts
  useEffect(() => {
    return () => {
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]);
        } catch {}
      }
    };
  }, []);

  // This is a logic-only component
  return null;
};
